import { isNil } from 'lodash';
import type { SettingControl } from './registry.types';

type NumberControl = Extract<SettingControl, { type: 'number' }>;

export type ParsedNumberEntry =
  | { readonly ok: true; readonly value: number | null }
  | { readonly ok: false; readonly error: string };

const boundsSentence = ({ min, max, integer }: NumberControl): string => {
  const noun = integer === true ? 'whole number' : 'number';
  if (isNil(min) && isNil(max)) {
    return `Enter a ${noun}.`;
  }
  if (isNil(max)) {
    return `Enter a ${noun} of at least ${min}.`;
  }
  if (isNil(min)) {
    return `Enter a ${noun} of at most ${max}.`;
  }
  return `Enter a ${noun} between ${min} and ${max}.`;
};

/**
 * What to persist for a number control, or why the entry was refused.
 *
 * A half-typed or out-of-bounds entry is refused: every one of these fields
 * feeds arithmetic where a negative or fractional value would be applied rather
 * than rejected, and the API refuses the same values.
 *
 * A cleared box reads as `null` only for a key the API actually unsets.
 * `buildConfigPatch` drops a `null` for any other key, so sending one there
 * would report a save that changed nothing and then show the old value again.
 *
 * @param clearable Whether an empty box may persist `null` for this key, from
 *   `CLEARABLE_SETTING_KEYS`.
 */
export const parseNumberEntry = (
  text: string,
  control: NumberControl,
  clearable: boolean,
): ParsedNumberEntry => {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return clearable
      ? { ok: true, value: null }
      : {
          ok: false,
          error: `This setting cannot be empty. ${boundsSentence(control)}`,
        };
  }

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    return { ok: false, error: boundsSentence(control) };
  }
  if (control.integer === true && !Number.isInteger(parsed)) {
    return { ok: false, error: boundsSentence(control) };
  }
  if (!isNil(control.min) && parsed < control.min) {
    return { ok: false, error: boundsSentence(control) };
  }
  if (!isNil(control.max) && parsed > control.max) {
    return { ok: false, error: boundsSentence(control) };
  }
  return { ok: true, value: parsed };
};

/**
 * Refuses a `url` control's value early, on the common mistakes the server's
 * `@IsUrl({ protocols: ['http','https'], require_protocol: true, disallow_auth:
 * true })` rejects. It is not a full mirror of validator.js: the dot check is a
 * rough stand-in for `require_tld`, so `https://example.com2` passes here and
 * fails server-side, and a bare IPv6 host is refused here but not there.
 *
 * @returns An error message, or undefined when the value is acceptable. An
 * empty or absent value is acceptable and means "clear it".
 */
export const validateHttpUrl = (
  value: string | null | undefined,
): string | undefined => {
  if (isNil(value) || value === '') {
    return undefined;
  }
  if (value !== value.trim()) {
    return 'Remove the leading or trailing spaces';
  }
  if (/[\s<>]/.test(value)) {
    return 'The link must not contain spaces or angle brackets';
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return 'Must be a full URL, including http:// or https://';
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'Only http and https links are allowed';
  }
  if (parsed.username !== '' || parsed.password !== '') {
    return 'The link must not embed a username or password';
  }
  if (!parsed.hostname.includes('.')) {
    return 'Must be a full domain name, for example reviews.example.com';
  }
  return undefined;
};
